/**
 * The CRM master-config API lives on the CRM backend, reached under the `/crm`
 * gateway prefix. `@clarix/api-client`'s singleton points at the workspace
 * gateway ROOT (for global `/auth` + `/me`), so it can't call `/config/...`
 * with the right base. Every consumer therefore injects its OWN `/crm`-scoped
 * `apiService` (built with `createScopedApi({ baseURL: <origin>/crm })`):
 *
 *   - the CRM remote injects the one from `services/configs/crmApi.ts`
 *   - the shell host injects a `/crm`-scoped client it builds at startup
 *
 * Both share the same auth (Bearer + X-Workspace-Slug from the auth store) and
 * global payload encryption, so the call is identical from either side. This is
 * how the shell's Master rail panel can render `useCategories()` without
 * importing any CRM-app internals.
 */
export interface MasterConfigApiService {
  get<T>(url: string): Promise<T>;
  post<T>(url: string, data?: unknown): Promise<T>;
  put<T>(url: string, data?: unknown): Promise<T>;
  delete<T>(url: string): Promise<T>;
}

let configured: MasterConfigApiService | null = null;

/** Inject the `/crm`-scoped apiService. Call once per consumer at startup. */
export function configureMasterConfigApi(apiService: MasterConfigApiService): void {
  configured = apiService;
}

/** Resolve the injected apiService, throwing a clear error if unconfigured. */
export function getMasterConfigApiService(): MasterConfigApiService {
  if (!configured) {
    throw new Error(
      '[@clarix/services-master-config] configureMasterConfigApi() was not called. ' +
        'Each consumer (CRM remote + shell host) must inject its /crm-scoped apiService at startup.',
    );
  }
  return configured;
}

/** CRM gateway-relative endpoints (same for every consumer). */
export const MASTER_CONFIG_ENDPOINTS = {
  /** GET — list all categories. */
  categories: '/config/categories',
  /** GET — append `/:code/values`. */
  categoryValues: '/config/categories',
  /** POST — create a new (user-defined) master category. */
  categoriesAdmin: '/admin/config/categories',
  /** POST/PUT/DELETE individual values. */
  valuesAdmin: '/admin/config/values',
  /** PUT — reorder values in a category. */
  valuesReorder: '/admin/config/values/reorder',
  /** POST — seed defaults. */
  seed: '/admin/config/seed',
  /** GET — approval reasons master list. */
  approvalReasons: '/masters/approval-reasons',
} as const;

/** React Query keys — identical array values to the CRM app's prior local keys. */
export const masterConfigKeys = {
  all: ['master-config'] as const,
  categories: ['master-config', 'categories'] as const,
  values: (categoryCode: string) => ['master-config', 'values', categoryCode] as const,
};
